// Capital vs. Risk allocation -- exposes low-weight assets that dominate
// portfolio volatility (Euler PCTR), with the HRP allocation as a reference.

import React, { useMemo } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { RiskDecompositionRow } from '../types';

interface Props {
    rows: RiskDecompositionRow[];
}

export const CapitalVsRiskChart: React.FC<Props> = ({ rows }) => {
    const data = useMemo(
        () =>
            rows.map((r) => ({
                ticker: r.ticker,
                capital: r.weight * 100,
                risk: r.pctr * 100,
                hrp: r.hrp_weight * 100,
            })),
        [rows],
    );

    return (
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
            <h3 className="text-lg font-bold text-slate-800">Capital vs. Risk Allocation</h3>
            <p className="text-xs text-slate-500 font-medium mt-1 mb-4">
                Where your money sits versus where your risk actually comes from.
            </p>
            <div className="h-[340px] -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="ticker" stroke="#64748b" tick={{ fontSize: 12 }} />
                        <YAxis
                            stroke="#64748b"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(v: number) => `${v.toFixed(0)}%`}
                            width={48}
                        />
                        <Tooltip
                            formatter={(value: number | string, name: string | number) => [
                                `${Number(value).toFixed(1)}%`,
                                name,
                            ]}
                            contentStyle={{
                                borderRadius: '8px',
                                border: 'none',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                            }}
                        />
                        <Legend wrapperStyle={{ paddingTop: '10px', fontSize: 12 }} />
                        <Bar dataKey="capital" name="Capital Weight" fill="#38bdf8" radius={[4, 4, 0, 0]} maxBarSize={36} />
                        <Bar dataKey="risk" name="Risk Contribution" fill="#f43f5e" radius={[4, 4, 0, 0]} maxBarSize={36} />
                        <Line
                            type="monotone"
                            dataKey="hrp"
                            name="HRP Suggested Weight"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={{ r: 4 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
